/**
 * Purpose:
 *   Tailwind CSS class constants for the Menu sidebar component.
 *
 * Notes:
 *   - Organized by component section (container, items, overlay)
 *   - Uses project color tokens from Tailwind config
 *   - Supports both desktop (fixed) and mobile (drawer) layouts
 *   - Supports collapsed (icons only) and expanded states
 *   - All items maintain consistent height for smooth transitions
 *   - Animation duration: 350ms synchronized across all elements
 */

// ============================================================
// SIDEBAR WRAPPER (for toggle button positioning)
// ============================================================

// Wrapper for desktop sidebar - allows button to overflow
export const MENU_WRAPPER_DESKTOP = `
  hidden desktop:block
  fixed left-0 top-16
  h-[calc(100vh-64px)]
  z-30
`;

// Wrapper width - EXPANDED
export const MENU_WRAPPER_EXPANDED = `
  w-64
`;

// Wrapper width - COLLAPSED
export const MENU_WRAPPER_COLLAPSED = `
  w-16
`;

// ============================================================
// SIDEBAR CONTAINER STYLES
// ============================================================

// Sidebar container - desktop (inside wrapper)
export const MENU_CONTAINER_DESKTOP = `
  absolute inset-0
  bg-menu border-r border-base-100
  overflow-y-auto hide-scrollbar
  will-change-transform
`;

// Sidebar container - mobile (drawer overlay)
export const MENU_CONTAINER_MOBILE = `
  desktop:hidden fixed inset-0 z-40
  transition-opacity duration-300 ease-out
`;

// Mobile container open state
export const MENU_CONTAINER_MOBILE_OPEN = `
  opacity-100 pointer-events-auto
`;

// Mobile overlay background
export const MENU_OVERLAY = `
  absolute inset-0 bg-black/50
  transition-opacity duration-300 ease-out
`;

// Mobile overlay open state
export const MENU_OVERLAY_OPEN = `
  opacity-100
`;

// Mobile drawer panel
export const MENU_DRAWER = `
  absolute left-0 top-0 bottom-0
  w-72 bg-menu
  transition-transform duration-300 ease-out
  overflow-y-auto hide-scrollbar
  shadow-xl
  will-change-transform
`;

// Drawer open state
export const MENU_DRAWER_OPEN = `
  translate-x-0
`;

// Drawer spacer matching fixed header height
export const MENU_DRAWER_HEADER_SPACER = `
  h-16
`;

// ============================================================
// NAVIGATION CONTAINER
// ============================================================

// Navigation list container
export const MENU_NAV = `
  flex flex-col py-4 gap-1
`;

// ============================================================
// MENU ITEM STYLES
// ============================================================

// Base menu item styles (simple link items)
export const MENU_ITEM_BASE = `
  flex items-center
  h-12 mx-2 rounded-lg
  cursor-pointer
  border border-transparent
  transition-all duration-200 ease-out
  hover:bg-base-0
  hover:border-blue-100
  focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2
  group relative
`;

// Menu item active state
export const MENU_ITEM_ACTIVE = `
  bg-green-100 text-green-500 hover:bg-green-100 hover:border-green-700
`;

// Menu item with children (expandable) - wrapper container
export const MENU_ITEM_EXPANDABLE_WRAPPER = `
  mx-2 rounded-lg
  transition-[border-color,background-color] duration-300 ease-out
`;

// Expandable wrapper active (has border when expanded)
export const MENU_ITEM_EXPANDABLE_WRAPPER_ACTIVE = `
  border border-blue-100 bg-base-0
`;

// Expandable header button styles
export const MENU_ITEM_EXPANDABLE_HEADER = `
  flex items-center
  h-12 rounded-lg w-full
  cursor-pointer
  border-b border-transparent
  transition-all duration-200 ease-out
  hover:bg-base-0
  hover:border-blue-100
  focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-inset
  group relative
`;

// ============================================================
// CHEVRON / TOGGLE ICON
// ============================================================

// Chevron icon for expandable items
export const MENU_CHEVRON = `
  transition-transform duration-300 ease-out
  flex-shrink-0
  will-change-transform
`;

// Chevron rotated (collapsed/closed state)
export const MENU_CHEVRON_COLLAPSED = `
  -rotate-0
`;

// Chevron rotated (expanded/open state)
export const MENU_CHEVRON_EXPANDED = `
  rotate-180
`;

// ============================================================
// SUBMENU STYLES
// ============================================================

// Sub-menu container with animated height
export const MENU_SUBMENU = `
  overflow-hidden
  will-change-[height]
`;

// Sub-menu collapsed state — children must not intercept clicks beneath the header
export const MENU_SUBMENU_COLLAPSED = `
  pointer-events-none invisible
`;

// Sub-menu inner padding
export const MENU_SUBMENU_INNER = `
  flex flex-col gap-1 py-1
`;

// Sub-menu item styles
export const MENU_SUBMENU_ITEM = `
  flex items-center gap-3
  h-11 px-4 mx-2 rounded-lg
  cursor-pointer
  border border-transparent
  transition-all duration-200 ease-out
  hover:bg-base-0
  hover:border-blue-100
  focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-inset
`;

// Sub-menu item active state
export const MENU_SUBMENU_ITEM_ACTIVE = `
  bg-green-100 text-green-500 hover:bg-green-100 hover:border-green-700
`;

// ============================================================
// COLLAPSE TOGGLE BUTTON
// ============================================================

// Toggle button - positioned on the right edge of the wrapper
export const MENU_TOGGLE_BUTTON = `
  absolute right-0 top-6
  translate-x-1/2
  flex items-center justify-center
  w-7 h-7 rounded-full
  bg-base-white border border-blue-1000
  shadow-md
  cursor-pointer
  transition-all duration-250 ease-out
  hover:bg-base-0 hover:shadow-lg hover:scale-110
  active:scale-95
  focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400
  z-50
  will-change-transform
`;

// Toggle icon
export const MENU_TOGGLE_ICON = `
  transition-transform duration-300 ease-out
  text-green-500
  will-change-transform
`;

// Toggle icon rotated (collapsed state - rotate to point right)
export const MENU_TOGGLE_ICON_COLLAPSED = `
  -rotate-90
`;

// Toggle icon expanded state - rotate to point left
export const MENU_TOGGLE_ICON_EXPANDED = `
  rotate-90
`;

// ============================================================
// TOOLTIP FOR COLLAPSED STATE
// ============================================================

// Tooltip container - only visible when parent has group class and is hovered
export const MENU_TOOLTIP = `
  absolute left-full ml-3 px-3 py-1.5
  bg-neutral-500 text-base-white rounded-md
  whitespace-nowrap b4
  opacity-0 scale-95 -translate-x-1
  transition-all duration-200 ease-out
  group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-0
  pointer-events-none
  z-50
  shadow-lg
`;

// Floating popover for collapsed menu categories
export const MENU_COLLAPSED_POPOVER = `
  fixed translate-y-[calc(50%_-_24px)] z-[999]
  min-w-56 max-w-72
  rounded-md border border-blue-100
  bg-base-white p-1
  shadow-xl
  transition-all duration-150 ease-out
`;

export const MENU_COLLAPSED_POPOVER_HIDDEN = `
  pointer-events-none
  translate-x-1 opacity-0
`;

export const MENU_COLLAPSED_POPOVER_VISIBLE = `
  pointer-events-auto
  translate-x-0 opacity-100
`;

export const MENU_COLLAPSED_POPOVER_HEADER = `
  p-2 b3 font-bold text-blue-500
`;

export const MENU_COLLAPSED_POPOVER_LIST = `
  flex flex-col gap-1
`;

export const MENU_COLLAPSED_POPOVER_ARROW = `
  pointer-events-none absolute -left-[14px]
  h-0 w-0 -translate-y-1/2
  border-y-[12px] border-y-transparent
  border-r-[14px] border-r-blue-100
`;

export const MENU_COLLAPSED_POPOVER_ARROW_INNER = `
  pointer-events-none absolute left-[2px] top-1/2
  h-0 w-0 -translate-y-1/2
  border-y-[11px] border-y-transparent
  border-r-[13px] border-r-base-white
`;

// ============================================================
// TEXT STYLES
// ============================================================

// Menu item text - uses b3 typography class (no color transition to avoid flash)
export const MENU_TEXT = `
  b3 text-neutral-500
`;

// Menu item text active (no color transition to avoid flash)
export const MENU_TEXT_ACTIVE = `
  b3 text-green-500 font-bold
`;

// ============================================================
// DIVIDER & SECTION
// ============================================================

// Menu section divider
export const MENU_DIVIDER = `
  border-t border-base-100 my-2 mx-4
`;

// Menu section label
export const MENU_SECTION_LABEL = `
  px-4 py-2 b3
  text-neutral-300 uppercase tracking-wider
`;
